import { Cancel, Check, Delete, Edit } from "@mui/icons-material";
import { IconButton, IconButtonProps } from "@mui/material";
import { FC, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { IStyledAttributes } from "@ryde/shared";
import { blueGreyMui, greenMui, greyMui, redMui } from "@ryde/theme";

import { FramerTooltip } from "../../../common";

enum EColorIconButtonTypes {
  EDIT = "EDIT",
  DELETE = "DELETE",
  CHECK = "CHECK",
  CANCEL = "CANCEL",
}

const colorIconButtonClass: { [key in EColorIconButtonTypes]: string } = {
  [EColorIconButtonTypes.EDIT]: "edit_color",
  [EColorIconButtonTypes.DELETE]: "delete_color",
  [EColorIconButtonTypes.CHECK]: "check_color",
  [EColorIconButtonTypes.CANCEL]: "cancel_color",
};

const iconByIconType = {
  [EColorIconButtonTypes.EDIT]: <Edit />,
  [EColorIconButtonTypes.DELETE]: <Delete />,
  [EColorIconButtonTypes.CHECK]: <Check />,
  [EColorIconButtonTypes.CANCEL]: <Cancel />,
};

const resolveIcon = (
  iconType: keyof typeof EColorIconButtonTypes,
): ReactElement => {
  if (iconType in iconByIconType) {
    return iconByIconType[iconType];
  }

  return <span />;
};

interface IColorIconButtonProps extends IconButtonProps {
  iconType: keyof typeof EColorIconButtonTypes;
  onClick?: () => void;
  tooltipContent?: string;
  tooltipDisabled?: boolean;
}

const ColorIconButtonStyled = styled(IconButton).attrs<IStyledAttributes>({
  "data-e2e": "icon-button-default-color",
})<{ $disabled?: boolean }>`
  > span {
    width: 24px;
    height: 24px;
  }

  &.${colorIconButtonClass.EDIT} svg {
    fill: ${({ $disabled }) => ($disabled ? greyMui._500 : blueGreyMui._500)};
  }
  &.${colorIconButtonClass.DELETE} svg {
    fill: ${({ $disabled }) => ($disabled ? greyMui._500 : redMui._300)};
  }
  &.${colorIconButtonClass.CANCEL} svg {
    fill: ${({ $disabled }) => ($disabled ? greyMui._500 : redMui._300)};
  }
  &.${colorIconButtonClass.CHECK} svg {
    fill: ${({ $disabled }) => ($disabled ? greyMui._500 : greenMui._300)};
  }
`;

export const ColorIconButton: FC<IColorIconButtonProps> = ({
  onClick,
  iconType,
  tooltipContent,
  tooltipDisabled = false,
  ...props
}) => {
  const { t } = useTranslation();

  const content = useMemo(() => {
    if (tooltipContent) {
      return tooltipContent;
    }

    switch (iconType) {
      case EColorIconButtonTypes.EDIT:
        return t("general.EDIT");
      case EColorIconButtonTypes.CANCEL:
        return t("general.CANCEL");
      case EColorIconButtonTypes.CHECK:
        return t("general.APPROVE");
      case EColorIconButtonTypes.DELETE:
        return t("general.ARCHIVE");
      default:
        return "";
    }
  }, [iconType, tooltipContent, t]);

  const Icon = resolveIcon(iconType);
  return (
    <ColorIconButtonStyled
      className={colorIconButtonClass[iconType]}
      onClick={() => onClick?.()}
      $disabled={props.disabled}
      {...props}
    >
      <FramerTooltip
        content={content}
        rtlStrippedBottomLeftPlacement
        tooltipDisabled={tooltipDisabled || props?.disabled}
      >
        {Icon}
      </FramerTooltip>
    </ColorIconButtonStyled>
  );
};
